import { metricHint, metricLabel } from "../football-intelligence/fiData";

/*
 * One canonical statistics presentation per context.
 *
 *  - Season totals (API-Football): every competition is listed on its own; the
 *    "all listed competitions" line is a sum of those rows and says so.
 *  - Detailed statistics (PitchAPI, private-research gate): always labelled
 *    with the sample (matches, minutes, competition), never merged with a
 *    season total.
 *  - API-Football fills the detailed view only for the few metrics whose
 *    meaning is the same, and only when PitchAPI has no sample.
 *  - Missing is "—", never 0.
 */

export const PLAYER_CATEGORIES = [
  { id: "attacking", title: "Attacking", keys: ["goals", "expected_goals", "npxg", "total_shots", "shots_on_target"] },
  { id: "creativity", title: "Creativity", keys: ["assists", "expected_assists", "xag", "chances_created", "key_passes", "xt_total"] },
  { id: "passing", title: "Passing", keys: ["passes_attempted", "accurate_passes", "pass_completion"] },
  { id: "progression", title: "Progression", keys: ["progressive_passes", "progressive_carries"] },
  { id: "defending", title: "Defending", keys: ["tackles", "interceptions", "recoveries", "clearances", "aerials_won", "defensive_actions"] },
  { id: "possession", title: "Possession", keys: ["touches", "take_ons", "take_ons_won", "dispossessed"] },
  { id: "discipline", title: "Discipline", keys: ["fouls", "yellow_cards", "red_cards"] },
];

const LABEL_OVERRIDES = {
  total_shots: "Shots",
  shots_on_target: "Shots on target",
  pass_completion: "Pass completion",
  passes_attempted: "Passes attempted",
  accurate_passes: "Passes completed",
  take_ons_won: "Take-ons won",
  take_ons: "Take-ons attempted",
};
const labelFor = (key) => LABEL_OVERRIDES[key] || metricLabel(key);

const finite = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

/** Compatible metrics the season feed can supply, as { key: {value, minutes} }. */
export function seasonMetricsFromCompetitions(competitions = []) {
  if (!competitions.length) return {};
  const sum = (pick) => {
    const vals = competitions.map(pick).map(finite).filter((v) => v !== null);
    return vals.length ? vals.reduce((a, b) => a + b, 0) : null;
  };
  const minutes = sum((c) => c?.games?.minutes ?? c?.minutes);
  const out = {};
  const put = (key, value) => {
    if (value !== null) out[key] = { value, minutes, unit: "total" };
  };
  put("goals", sum((c) => c?.goals?.total));
  put("assists", sum((c) => c?.assists ?? c?.goals?.assists));
  put("total_shots", sum((c) => c?.shots?.total));
  put("shots_on_target", sum((c) => c?.shots?.on));
  put("yellow_cards", sum((c) => c?.cards?.yellow));
  put("red_cards", sum((c) => c?.cards?.red));
  return out;
}

const rowFor = (key, metric, { withPer90 }) => {
  const value = finite(metric?.value);
  return {
    key,
    label: labelFor(key),
    hint: metricHint(key),
    value,
    per90: withPer90 ? finite(metric?.per90) : null,
    sample: finite(metric?.sample),
  };
};

/** Derived, clearly computed ratio; only when both inputs are recorded and attempts > 0. */
export function withDerived(metrics = {}) {
  const out = { ...metrics };
  const att = finite(metrics.passes_attempted?.value);
  const ok = finite(metrics.accurate_passes?.value);
  if (att && ok !== null && att > 0) out.pass_completion = { value: (ok / att) * 100, unit: "percent", derived: true };
  return out;
}

/**
 * @param pitch  PitchAPI player payload ({status, metrics, sample_size}) or null
 * @param seasonCompetitions API-Football season_stats.competitions[]
 * @param scopeLabel e.g. "Last 10 matches · Bundesliga" supplied by the filter UI
 */
export function buildStatisticsView({ pitch = null, seasonCompetitions = [], scopeLabel = "" } = {}) {
  const pitchMetrics = pitch?.status === "AVAILABLE" ? withDerived(pitch.metrics || {}) : null;
  const usePitch = !!pitchMetrics && Object.keys(pitchMetrics).length > 0;
  const metrics = usePitch ? pitchMetrics : withDerived(seasonMetricsFromCompetitions(seasonCompetitions));
  const source = usePitch ? "pitchapi" : Object.keys(metrics).length ? "api_football" : "none";

  const sections = PLAYER_CATEGORIES.map((cat) => ({
    id: cat.id,
    title: cat.title,
    rows: cat.keys.filter((k) => metrics[k]).map((k) => {
      const row = rowFor(k, metrics[k], { withPer90: usePitch });
      if (k === "pass_completion") row.percent = true;
      return row;
    }),
  })).filter((s) => s.rows.length);

  const minutes = usePitch
    ? Math.max(0, ...Object.values(pitchMetrics).map((m) => finite(m?.minutes) || 0))
    : finite(Object.values(metrics)[0]?.minutes);
  const warnings = [];
  if (usePitch && minutes > 0 && minutes < 270) {
    warnings.push(`Only ${Math.round(minutes)} minutes in this sample, so per-90 figures are volatile.`);
  }
  if (source === "api_football") {
    warnings.push(
      "Detailed provider statistics are not available for this player; showing the season totals that every provider defines the same way."
    );
  }
  const sampleNote = usePitch
    ? `${scopeLabel || "Selected sample"} · ${pitch.sample_size ?? "?"} matches${rangeLabel(pitch.date_range) ? ` · ${rangeLabel(pitch.date_range)}` : ""}${minutes ? ` · ${Math.round(minutes)} min` : ""}`
    : source === "api_football"
      ? "Season totals · sum of the competitions listed under Season"
      : "";
  return {
    source,
    sourceLabel: source === "pitchapi" ? "PitchAPI" : source === "api_football" ? "API-Football" : "",
    sampleNote,
    sections,
    warnings,
  };
}

/** Per-competition season rows (never mixed), plus the labelled sum. */
export function buildSeasonView(seasonCompetitions = []) {
  const rows = seasonCompetitions.map((c) => ({
    key: `${c.league_id}-${c.league}`,
    league_id: c.league_id,
    competition: c.league || "—",
    apps: finite(c?.games?.appearences),
    minutes: finite(c?.games?.minutes ?? c?.minutes),
    goals: finite(c?.goals?.total),
    assists: finite(c?.assists ?? c?.goals?.assists),
    rating: c?.games?.rating ? Number(c.games.rating) : null,
  }));
  const sum = (k) => {
    const v = rows.map((r) => r[k]).filter((x) => x !== null);
    return v.length ? v.reduce((a, b) => a + b, 0) : null;
  };
  return {
    rows,
    total: rows.length > 1 ? { apps: sum("apps"), minutes: sum("minutes"), goals: sum("goals"), assists: sum("assists") } : null,
    totalLabel: "All listed competitions · sum of the rows below",
    ...(seasonCompetitions.some(c=>c.source === "pitchapi") ? {
      source: "mixed", season: seasonCompetitions.find(c=>c.season)?.season,
      warning: "Includes available recorded season matches. Coverage may be incomplete."} : {}),
  };
}

const monthYear = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : "");

/** "Oct 2024 – Oct 2025" from the payload's [first, last] kickoff pair. */
export function rangeLabel(range) {
  if (!Array.isArray(range) || range.length < 2 || !range[0] || !range[1]) return "";
  const [a, b] = [monthYear(range[0]), monthYear(range[1])];
  return a === b ? a : `${a} – ${b}`;
}

/**
 * Explains a visible disagreement between a season total and a sampled total
 * for the same stat, so two numbers are never shown unexplained. The two
 * windows are different periods, so the text says so and never claims one is
 * a subset of the other.
 */
export function explainDifference({ seasonGoals, sampleGoals, sampleMatches, sampleRange }) {
  if (seasonGoals === null || seasonGoals === undefined || sampleGoals === null || sampleGoals === undefined) return "";
  if (seasonGoals === sampleGoals) return "";
  const when = rangeLabel(sampleRange);
  return `Season total: ${seasonGoals} goals across the current season's listed competitions (API-Football). Sample: ${sampleGoals} goals in the selected ${sampleMatches ?? "?"} matches${when ? ` played ${when}` : ""} (PitchAPI). They cover different periods, so they are not directly comparable.`;
}

/** Season records are independent of the last-N / single-match analytics sample. */
export function recordedSeasonView(summary) {
  if (summary?.status !== "AVAILABLE") return {rows: [], total: null};
  const rows = (summary.competitions || []).map(row => ({...row, key: row.league_id}));
  const sum = key => {
    const values = rows.map(r => r[key]).filter(v => Number.isFinite(v));
    return values.length ? values.reduce((a,b) => a+b,0) : null;
  };
  return {rows, season: summary.season, source: "pitchapi", warning: summary.warning,
    total: rows.length > 1 ? Object.fromEntries(["apps","minutes","goals","assists"].map(k=>[k,sum(k)])) : null,
    totalLabel: "All recorded competitions · partial season coverage"};
}

export function chooseSeasonView(primaryCompetitions, recordedSummary) {
  const primary = buildSeasonView(primaryCompetitions);
  const recorded = recordedSeasonView(recordedSummary);
  const hasAppearances = rows => rows.some(r => r.apps > 0 || r.minutes > 0);
  if (!hasAppearances(primary.rows)) return hasAppearances(recorded.rows) ? recorded : primary;
  // Add a missing competition only through a verified provider league ID.
  // Do not merge on a similar name or sum overlapping provider totals.
  const rows = primary.rows.map(row => {
    const match = recorded.rows.find(r => r.api_football_league_id != null && String(r.api_football_league_id) === String(row.league_id));
    return !(row.apps > 0 || row.minutes > 0) && match ? {...match, key: row.key, recorded: true} : row;
  });
  const known = new Set(primary.rows.map(r => String(r.league_id)));
  const additions = recorded.rows.filter(r => r.api_football_league_id != null && !known.has(String(r.api_football_league_id)));
  if (!additions.length && !rows.some(r => r.recorded)) return primary;
  rows.push(...additions);
  const sum = key => { const values=rows.map(r=>r[key]).filter(Number.isFinite); return values.length ? values.reduce((a,b)=>a+b,0) : null; };
  return {rows, season: recorded.season, source: "mixed", total: rows.length > 1 ? Object.fromEntries(["apps","minutes","goals","assists"].map(k=>[k,sum(k)])) : null,
    totalLabel: "All listed competitions · available recorded appearances",
    warning: "Includes recorded cup/league appearances where season-feed data is missing. Coverage may be incomplete; overlapping competitions are counted once."};
}
